import { useRef, useState } from 'react';
import Cropper from 'react-cropper';
import { Dialog, DialogContent, DialogActions, Button } from 'src/components';
import { useIsMobile } from 'src/hooks';

export default function CropModal({ open, onClose, imageSrc, aspect = 1, onCropComplete }) {
    const isMobile = useIsMobile();
    const cropperRef = useRef(null); // Use ref instead of useState for synchronous access
    const [isReady, setIsReady] = useState(false);

    const handleSave = () => {
        // Access the internal cropper instance from the React ref
        const cropper = cropperRef.current?.cropper;

        if (!cropper || !isReady) return;

        const canvas = cropper.getCroppedCanvas({
            fillColor: '#fff',
            imageSmoothingQuality: 'high',
            // Crucial: iOS and some browsers return null if the canvas is too large.
            // 4096px is the safe hardware limit for most mobile devices.
            maxWidth: 4096,
            maxHeight: 4096,
        });

        if (!canvas) {
            console.error('Canvas is null. Possible reasons: Tainted canvas (CORS) or image too large.');
            return;
        }

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
                onCropComplete(file);
                onClose();
            }
        }, 'image/jpeg', 0.9);
    };

    if (!imageSrc) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth={!isMobile}
        >
            <DialogContent sx={{ p: 0, height: isMobile ? 'calc(100vh - 120px)' : '70vh', overflow: 'hidden' }}>
                <Cropper
                    ref={cropperRef}
                    src={imageSrc}
                    style={{ height: '100%', width: '100%' }}
                    aspectRatio={aspect}
                    guides={true}
                    viewMode={1}
                    dragMode="move"
                    background={false}
                    responsive={true}
                    autoCropArea={0.8}
                    checkOrientation={false}
                    // Essential for external URLs (S3/Cloudinary) to prevent "tainted" canvas
                    crossOrigin="anonymous" 
                    ready={() => {
                        setIsReady(true);
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained"
                    disabled={!isReady}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
}
